import { Box, Flex, Text } from '@chakra-ui/react';
import { type FC } from 'react';
import { useTranslation } from 'react-i18next';

import {
  CardContent,
  type CardContentGridProps,
} from '$/components/common/Card/CardContent';
import { useAuthorization } from '$/components/core/Authentication/hooks/useAuthorization';
import {
  MaterialCardContextMenu,
  type MaterialCardEditMode,
} from '$/components/core/Collection/MaterialCard/MaterialCardContextMenu';
import {
  type Material,
  type MaterialType,
} from '$/services/usecases/materials/mapper/materials';

interface Props extends CardContentGridProps {
  material: Material;
  editMode?: MaterialCardEditMode;
  materialType?: MaterialType;
  labelOverwrite?: string;
  disableBrightness?: boolean;
}

export const MaterialCard: FC<Props> = ({
  material,
  labelOverwrite,
  editMode = 'favorite',
  materialType,
  disableBrightness = false,
  ...props
}) => {
  const { t } = useTranslation();
  const { hasPermission } = useAuthorization();

  const cursor = props.onClick != null ? 'pointer' : 'default';
  const bg = !material.url() ? (material.hex ?? 'transparent') : 'transparent';
  const label = labelOverwrite ?? material.name;

  const showProLabel =
    material.isProMaterial && !hasPermission('Use_Pro_Materials');

  return (
    <Box pos='relative' overflow='hidden' cursor={cursor}>
      <MaterialCardContextMenu
        editMode={editMode}
        material={material}
        materialType={materialType}
      />

      {showProLabel && (
        <Flex
          pos='absolute'
          top='7%'
          left='-40%'
          justify='center'
          w='100%'
          bg='primaryButton.background'
          transform='rotate(-45deg)'
          userSelect='none'
        >
          <Text color='reverseText' fontSize='xs' fontWeight='bold'>
            {t('roles.proLabel')}
          </Text>
        </Flex>
      )}

      <CardContent
        bg={bg}
        label={label}
        image={material.url({ size: '300' })}
        brightness={disableBrightness ? undefined : material.lrv}
        {...props}
      />
    </Box>
  );
};
